import { SetStateAction } from "react";
import { makeCygnusApiCall } from "../../../api/api";
import { Approver } from "../../../Reusables/components/ClientApproverSetting/ClientApproverSetting.utils";

/**
 * Fetches all users eligible for approval settings.
 *
 * @param setFetchUsersProgress - Callback to toggle loading spinner
 * @param setUsers - Callback to set the list of users
 */
export const fetchAllUsers = async (
  setUsers: { (val: SetStateAction<Approver[]>): void; (arg: Approver[]): void }
) => {

  const response = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/subscriptions/roles/users/`,
    show_message: true,
    customized_error_message: "Failed to load users",
    isblob: false,
    forMDM: false,
  });

  if (response.success) {
    setUsers(response.data ?? []);
    return response.data;
  }
  
  return [];
};

/**
 * Fetches existing approver configuration for a client or firm.
 *
 * @param clientMasterId - ID of the client
 * @param clientTypeCode - Type code associated with the client
 * @param setFetchApproversProgress - Callback to toggle loading spinner
 * @param setApprovers - Callback to set list of approvers
 * @param setApprovalCheck - Callback to toggle approval required flag
 * @param setApprovalType - Callback to set approval workflow type
 */
export const fetchExistingApprovers = async (
  clientMasterId: number,
  clientTypeCode: string | number | null,
  setInitialApprovers: { (val: SetStateAction<Approver[]>): void; (arg: Approver[]): void },
  setSelectedApprovers: { (val: SetStateAction<Approver[]>): void; (arg: Approver[]): void },
  setApprovalCheck: { (val: SetStateAction<boolean>): void; (arg: boolean): void },
  setApprovalType: { (val: SetStateAction<"Parallel" | "Sequential">): void; (arg: "Parallel" | "Sequential"): void },
  setFetchApproversProgress: { (val: SetStateAction<boolean>): void; (arg: boolean): void },
  reapplyRules?: boolean
) => {
  setFetchApproversProgress(true);

  const endpoint = clientMasterId
    ? `/form/clientapprovers/?ClientMasterId=${clientMasterId}&ReapplyRules=${reapplyRules}`
    : `/form/approvalsetting/?ClientTypeCode=${clientTypeCode}`;

  const response = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: endpoint,
    show_message: true,
    customized_error_message: "Failed to load existing approvers",
    isblob: false,
    forMDM: true,
  });

  if (response.success && response.data?.length > 0) {
    const data = response.data;
    setInitialApprovers(data);
    setSelectedApprovers(data);
    setApprovalCheck(data[0].ApprovalRequired === "N");
    setApprovalType(data[0].ApprovalType ?? "Parallel");
  } else {
    setInitialApprovers([]);
    setSelectedApprovers([]);
  }

  setFetchApproversProgress(false);
};

/**
 * Saves updated approver settings for a client or firm.
 *
 * @param clientMasterId - ID of the client
 * @param clientTypeCode - Type code associated with the client
 * @param approvalCheck - Whether approval is required or not
 * @param selectedApprovers - List of selected approvers
 * @param approvalRadio - The type of approval workflow ("Parallel" or "Sequential")
 * @param setSaveApproversProgress - Callback to toggle loading spinner
 * @param onSuccess - Callback to run after successful save
 */
export const saveApproverSettings = async (
  clientMasterId: number,
  clientTypeCode: string | number | null,
  approvalCheck: boolean,
  selectedApprovers: Approver[],
  approvalRadio: "Parallel" | "Sequential",
  setSaveApproversProgress: { (val: SetStateAction<boolean>): void; (arg: boolean): void },
  onSuccess: () => void
) => {
  setSaveApproversProgress(true);

  const approverPayload = approvalCheck
    ? [
        {
          ClientTypeCode: clientTypeCode,
          ClientMasterId: clientMasterId,
          ApprovalRequired: "N",
          FirmDetailId: 0,
        },
      ]
    : selectedApprovers.map((a) => ({
        ...a,
        ApprovalRequired: "Y",
        OnboardingClientApproverId: a.OnboardingClientApproverId,
        ClientMasterId: clientMasterId,
        ApprovalType: approvalRadio,
        ClientTypeCode: clientTypeCode
      }));

  const endpoint = clientMasterId
    ? `/form/clientapprovers/`
    : `/form/approvalsetting/`;

  const response = await makeCygnusApiCall({
    type: "POST",
    url: endpoint,
    payload: approverPayload,
    show_message: true,
    customized_success_message: "Approval settings saved successfully",
    customized_failure_message: "Failed to save approvers",
    customized_error_message: "Failed to save approvers",
    isblob: false,
    forMDM: true,
  });

  setSaveApproversProgress(false);
  if (response.success) {
    onSuccess();
  }
};
